import { authRequestError } from '@/utils/authRequestError';
import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField, Radio, FormControlLabel } from '@mui/material';

import { InputWrapper, MultiInputWrapper } from '@/components/Container/AuthContainer/styles';
import { Button } from '@/components/UI/Button';
import { Text } from '@/components/UI/Typography/Text';
import Link from '@/components/UI/Link';
import { Box } from '@/layout/Box';
import { successHandler } from '@/utils/toastHandler';

import useAuthValidation from '@/hooks/requireSchema/useAuthValidation';
import useRegistrationValidation from '@/hooks/requireSchema/useRegistrationValidation';
import { resendEmailVerificationCode, smsRegister, sendSMSCode } from '@/api/auth.api';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withDmpPurchaseReturnPath } from '@/components/DmpV2/purchaseReturnPath';
import { v2UiEnabled } from '@/components/DmpV2/features';
import { signIn } from '@/redux/auth/auth.slice';
import { Flex } from '@/layout';
import { theme } from '@/theme/Theme';
import useResize from '@/hooks/useResize';
import TurnstileWidget from '@/components/TurnstileWidget';
import { getTurnstileSiteKey } from '@/utils/turnstile';
import ExternalProviderOptions from '../ExternalProviderOptions';
import type { ExternalProviderDescriptor } from '@/types/externalAuth';
import { FormSelect } from '@/components/UI/Select';
import {
  buildSignUpRegistrationPayload,
  HONG_KONG_COUNTRY_CODE,
  HONG_KONG_COUNTRY_CODE_OPTIONS,
} from './registrationPayload';

interface FormStepperProps {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  setUserMail: React.Dispatch<React.SetStateAction<string>>;
  setShowAccount: React.Dispatch<React.SetStateAction<string>>;
  externalProviders?: ExternalProviderDescriptor[];
  onExternalRegistration?: (provider: string, turnstileToken: string | null) => Promise<boolean>;
}

const FormStep1 = ({
  setShowAccount,
  setActiveStep,
  setUserMail,
  externalProviders = [],
  onExternalRegistration,
}: FormStepperProps) => {
  // @ts-ignore
  const { t, i18n } = useTranslation();
  const { isMobile } = useResize();
  const [selectedRadio, setSelectedRadio] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileSiteKey = getTurnstileSiteKey();
  const {
    handleSubmit,
    control,
    setError,
    setValue,
    register,
    formState: { isValid },
  } = useForm<SMSRegisterRequired>({
    mode: 'onChange',
    defaultValues: {
      email: '',
      otp_method: 'email',
      phone_country_code: '',
      phone_number: '',
    },
  });

  const { emailRegister } = useAuthValidation();
  const { countryCodeRegister, mobileRegister } = useRegistrationValidation().companyInfo();

  const onSubmit = handleSubmit(async (data) => {
    const payload = buildSignUpRegistrationPayload({
      ...data,
      ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
    });

    try {
      // 送出驗證信
      const res = (await smsRegister(payload)).data;
      if (res.success) {
        if (payload.otp_method === 'sms') {
          setShowAccount(payload.phone_number);
        } else {
          setShowAccount(payload.email);
        }
        setUserMail(payload.email);
        setActiveStep(2);
      }
    } catch (error) {
      setError('email', {
        type: 'server',
        message: authRequestError(error, t),
      });
    } finally {
      if (turnstileSiteKey) {
        setTurnstileToken(null);
        setTurnstileResetKey((current) => current + 1);
      }
    }
  });

  const handleRadioChange = (index: number) => {
    setSelectedRadio(index);
    setValue('otp_method', index === 0 ? 'email' : 'sms');

    // 除了清空country code和phone number外, 還需移除必填驗證
    if (index === 0) {
      register('phone_country_code', { required: false });
      register('phone_number', { required: false });
      setValue('phone_country_code', '');
      setValue('phone_number', '');
    } else {
      register('phone_country_code', countryCodeRegister);
      register('phone_number', mobileRegister);
      setValue('phone_country_code', HONG_KONG_COUNTRY_CODE, { shouldValidate: true });
    }
  };

  return (
    <>
      <form onSubmit={onSubmit}>
        <Text marginTop={'15px'}>
          {t('pages.auth.signUp.illustrate.step1')}{' '}
          <Link to={`/${i18n.language || 'en'}/volunteerRegistration?step=1`}>
            {t('pages.auth.signUp.illustrate.pblLawyerLink')}
          </Link>
        </Text>

        <Box marginTop={30}>
          <Flex flexDirection={isMobile ? 'column' : 'row'}>
            <Flex flexDirection="column" gridGap="10px">
              <div className="radio">
                <FormControlLabel
                  control={
                    <Radio
                      color="success"
                      checked={selectedRadio === 0}
                      onChange={() => handleRadioChange(0)}
                      name="opt-buttons"
                    />
                  }
                  label={t('pages.auth.otpMethods.email')}
                  labelPlacement="end"
                />
              </div>
              {/* <Text>
              Get OTP via email
            </Text> */}
            </Flex>
            <Flex flexDirection="column" gridGap="10px">
              <div className="radio">
                <FormControlLabel
                  control={
                    <Radio
                      color="success"
                      onChange={() => handleRadioChange(1)}
                      checked={selectedRadio === 1}
                      name="opt-buttons"
                    />
                  }
                  label={t('pages.auth.otpMethods.sms')}
                  labelPlacement="end"
                />
              </div>
            </Flex>
          </Flex>
        </Box>
        <InputWrapper>
          <Controller
            control={control}
            defaultValue=""
            name={'email'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={t('form.email.label')}
                placeholder={t('form.email.placeholder')}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={emailRegister}
          />
        </InputWrapper>
        {selectedRadio === 1 && (
          <MultiInputWrapper>
            <Box style={{ width: isMobile ? '100%' : '170px' }}>
              <FormSelect
                control={control}
                data={HONG_KONG_COUNTRY_CODE_OPTIONS}
                inputLabel={t('form.countryCode.label')}
                selectLabel={t('form.countryCode.label')}
                name={'phone_country_code'}
                defaultValue={HONG_KONG_COUNTRY_CODE}
                disabled
                register={countryCodeRegister}
              />
            </Box>
            <Controller
              control={control}
              defaultValue=""
              name={'phone_number'}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  label={t('form.mobile.label')}
                  placeholder={t('form.mobile.placeholder')}
                  variant="standard"
                  value={value}
                  sx={{ width: '100%' }}
                  onChange={onChange}
                  error={!!error}
                  helperText={error ? error.message : null}
                  autoComplete="off"
                  inputProps={{ inputMode: 'numeric' }}
                />
              )}
              rules={mobileRegister}
            />
          </MultiInputWrapper>
        )}
        {turnstileSiteKey && (
          <Box marginBottom={15}>
            <TurnstileWidget
              key={turnstileResetKey}
              siteKey={turnstileSiteKey}
              action="member_registration"
              onTokenChange={setTurnstileToken}
            />
          </Box>
        )}
        <Box>
          <Button
            size={'lg'}
            width={isMobile ? '100%' : 'unset'}
            variant={'primary'}
            rounded={false}
            marginBottom={15}
            disabled={!isValid || (!!turnstileSiteKey && !turnstileToken)}
            type={'submit'}
          >
            {t('buttons.next')}
          </Button>
        </Box>
      </form>
      <ExternalProviderOptions
        mode="register"
        providers={externalProviders}
        disabled={Boolean(turnstileSiteKey) && !turnstileToken}
        onSelect={async (provider) => {
          const started = await onExternalRegistration?.(provider, turnstileToken);
          if (started === false && turnstileSiteKey) {
            setTurnstileToken(null);
            setTurnstileResetKey((current) => current + 1);
          }
        }}
      />
      <Text>
        {t('pages.auth.signUp.prompt.step1.context')} <Link to={'/auth/signin'}>{t('buttons.signIn')}</Link>
      </Text>
    </>
  );
};

interface userMailProps {
  userMail: string;
  showAccount: string;
}

const FormStep2 = ({ userMail, showAccount }: userMailProps) => {
  // @ts-ignore
  const { t } = useTranslation();
  const { isTablet, isMobile } = useResize();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isResending, setIsResending] = useState(false);
  const [resendCounter, setResendCounter] = useState(60);
  const buttonController = isResending || resendCounter > 0;
  const [attemptCounter, setAttemptCounter] = useState(0);

  const {
    handleSubmit,
    control,
    setError,
    formState: { isDirty, isSubmitting },
  } = useForm<{ verificationCode: string }>();
  const { verificationCode } = useAuthValidation();

  const sendNewCode = async () => {
    if (buttonController) return;
    setIsResending(true);
    try {
      await resendEmailVerificationCode({ email: userMail });
      setResendCounter(60);
      successHandler(t('success.resendCode'));
    } catch (error) {
      setError('verificationCode', { type: 'server', message: authRequestError(error, t) });
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    if (resendCounter <= 0) return;
    const timer = setTimeout(() => setResendCounter((remaining) => Math.max(0, remaining - 1)), 1000);
    return () => clearTimeout(timer);
  }, [resendCounter]);

  const onSubmit = handleSubmit(async (formData) => {
    const data: SMSVerificationRequired = {
      ...formData,
      email: userMail,
    };

    try {
      const res = (await sendSMSCode(data)).data;
      if (res.success) {
        dispatch(
          signIn({
            isLogin: true,
            accessToken: '',
            registDone: false,
            userData: {
              email: userMail,
              verificationCode: data.verificationCode,
              registration_token: res.data.registration_token,
            },
          })
        );
        navigate(
          v2UiEnabled()
            ? withDmpPurchaseReturnPath('/registration?step=1', searchParams.get('path'))
            : '/registration?step=1'
        );
      }
    } catch (error) {
      setAttemptCounter((prev) => prev + 1);
      setError('verificationCode', {
        type: 'server',
        message: t('error.verification'),
      });
    }
  });

  return (
    <>
      <form onSubmit={onSubmit}>
        <Text marginTop={'15px'}>
          {t('pages.auth.signUp.illustrate.step2-prev')}
          <span style={{ color: theme.colors.primary500 }}> {showAccount}</span>. <br />{' '}
          {t('pages.auth.signUp.illustrate.step2-last')}
        </Text>
        <InputWrapper>
          <Controller
            control={control}
            defaultValue=""
            name={'verificationCode'}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                label={`${t('form.verificationCode.label')} ${
                  attemptCounter > 0 ? `(${t('attempt')} ${attemptCounter})` : ''
                }`}
                placeholder={t('form.verificationCode.placeholder')}
                variant="standard"
                value={value}
                sx={{ width: '100%' }}
                onChange={onChange}
                error={!!error}
                helperText={error ? error.message : null}
                autoComplete="off"
              />
            )}
            rules={verificationCode}
          />
        </InputWrapper>
        <Flex gridGap={'15px'} flexDirection={isMobile ? 'column' : 'row'}>
          <Button size={'lg'} variant={'primary'} rounded={false} disabled={!isDirty || attemptCounter > 4}>
            {t('buttons.verify')}
          </Button>
          {attemptCounter < 5 && !buttonController && (
            <Button type={'button'} onClick={sendNewCode} size={'lg'} variant={'primary'} rounded={false}>
              {t('buttons.resendCode')}
            </Button>
          )}
          {buttonController && (
            <Button type={'button'} size={'lg'} variant={'normal'} rounded={false}>
              {`${t('pages.auth.signUp.prompt.step2.wait')} ${resendCounter}${t(
                'pages.auth.signUp.prompt.step2.resend'
              )}`}
            </Button>
          )}
        </Flex>
      </form>
    </>
  );
};

export { FormStep1, FormStep2 };
